import React, { useMemo } from "react";

import clsx from "clsx";
import { compact } from "lodash";

import { type FetchOrder_me_order } from "@north-beam/nb-client-side";
import { CustomerTypes, emptyText } from "@north-beam/nb-common";
import { useIsAllowedToSeeCustomerDetails } from "@pages/orders/use-is-allowed-to-see-customer-details";
import { Card } from "@shared/card";
import { CardDetails } from "@shared/card-details";
import {
  generateAnonBase64Hash,
  generateAnonEmailIfNecessary,
} from "@utils/anonymous";
import { usePrefs } from "@utils/client-side-preferences";

export function CustomerDetails({ order }: { order: FetchOrder_me_order }) {
  const prefs = usePrefs();
  const isAnon = prefs.isDemoMode;

  const isAllowedToSeeCustomerDetails = useIsAllowedToSeeCustomerDetails();

  const shippingInfo: string[] = useMemo(() => {
    return order.shippingAddress1
      ? compact([
          order.shippingAddress1,
          order.shippingAddress2,
          `${order.shippingCity}${
            order.shippingProvince ? `, ${order.shippingProvince}` : ""
          }${order.shippingZip ? ` ${order.shippingZip}` : ""}${
            order.shippingCountry ? `, ${order.shippingCountry}` : ""
          }${
            order.shippingCountryCode ? ` (${order.shippingCountryCode})` : ""
          }`,
        ])
      : [emptyText];
  }, [order]);

  const orderType = useMemo(() => {
    const customerType = order.orderType?.trim();
    if (customerType) {
      return `${
        CustomerTypes.contains(customerType)
          ? CustomerTypes.getLabel(customerType)
          : customerType
      } purchase`;
    }
    return emptyText;
  }, [order]);

  if (!isAllowedToSeeCustomerDetails) {
    return (
      <Card className={clsx(" mb-5 pt-3 pb-5 bg-v2-primary rounded-md")}>
        <p className="text-lg font-semibold">Customer Details</p>
        <p>
          Unfortunately you cannot view this data as you do not have sufficient
          permissions.
        </p>
        <div>
          If this seems incorrect, please direct your account owner to log a
          support ticket with our customer success team.
        </div>
      </Card>
    );
  }

  return (
    <CardDetails
      title="Customer Details"
      className="mb-5"
      details={[
        {
          heading: "Id",
          items: [
            generateAnonBase64Hash({
              value: order.customerId?.trim(),
              prefix: "id",
              doAnon: isAnon,
            }) ?? emptyText,
          ],
        },
        {
          heading: "Name",
          items: [
            generateAnonBase64Hash({
              value: order.customerFirstName?.trim(),
              prefix: "customer",
              doAnon: isAnon,
            }) ?? emptyText,
          ],
        },
        {
          heading: "Email",
          items: [
            generateAnonEmailIfNecessary(order.customerEmail?.trim(), isAnon) ??
              emptyText,
          ],
        },
        {
          heading: "Type",
          items: [orderType],
        },
        {
          heading: "Address",
          items: isAnon
            ? [
                generateAnonBase64Hash({
                  value: order.shippingAddress1,
                  prefix: "address",
                  doAnon: isAnon,
                }) || emptyText,
              ]
            : shippingInfo,
        },
        {
          heading: "Phone",
          items: isAnon
            ? [
                generateAnonBase64Hash({
                  value: order.customerPhone,
                  prefix: "phone",
                  doAnon: isAnon,
                }) || emptyText,
              ]
            : [order.customerPhone || emptyText],
        },
      ]}
    />
  );
}
